<template>
  <div
    :class="[$style.doughnut_chart_wrapper,
             config.large ? $style.doughnut_chart_wrapper_large : null,
             $style[labelPosition],
             'export_html_pie_chart_report_wrapper'
    ]"
    :style="{position: 'relative'}"
  >
    <div
      ref="chart"
      :class="[$style.chart_body, 'js_export_report_dymanic_chart']"
    />
    <div
      v-if="showStubDescription"
      ref="stubDesc"
      :class="$style.stub_desc"
      :style="stubDescPosition"
    >
      <span>{{ stubDescription }}</span>
    </div>
    <div
      v-if="config.total"
      :class="[$style.total_label, 'export_html_pie_chart_total_label']"
      :style="totalLabelStyles"
    >
      {{ `${config.total.label}:` }} <span>{{ `${aggrQuantity}` }}</span>
    </div>
    <div :class="[$style.legend, 'export_html_pie_chart_report_legend', $style[`legend_${config.labelStyle}`]]">
      <tooltip
        v-for="item in chartItems"
        :class="isGrayedOut(item.name) && 'disabled'"
        :key="item.id"
        :content="item.originalName"
        :position="'top'"
        :delay="100"
        :hide="!isOverflowed[item.name] || isGrayedOut(item.name)"
      >
        <template #body>
          <div
            :class="[
              $style.legend__item,
              'export_html_pie_chart_report_legend_item',
              isGrayedOut(item.name) && $style.grayed_out,
            ]"
            @mouseover="highlightSeries(item.name)"
            @mouseleave="downplay(item.name)"
            @click="toggleSelect(item.name)"
          >
            <div
              :class="[$style.legend_color_box, 'export_html_pie_chart_report_legend_box']"
              :style="{backgroundColor: item.color, width: config.legendStyles.boxWidth, height: config.legendStyles.boxHeight}"
            />
            <div
              ref="legendName"
              :class="[$style.legend_name, 'export_html_pie_chart_report_legend_name']"
              :style="{fontSize: config.legendStyles.fontSize}"
            >
              {{ item.originalName }}
            </div>
          </div>
        </template>
      </tooltip>
    </div>
  </div>
</template>

<script>
import * as echarts from '../../../../libs/echarts';
import Tooltip from '../tooltips/tooltip.vue';

const tooltipConfig = tooltipTitle => ({
  trigger: 'item',
  formatter: params => `<div class="chart_tooltip">
    <div class="tooltip_title">${tooltipTitle}</div>
    <div class="tooltip_info">
      <div class="marker">${params.marker}</div>
      <div class="tooltip_name">${params.data.originalName}</div>
      <div class="tooltip_qty">${params.data.qty}</div>
      <div class="tooltip_value">${params.value}%</div>
    </div>
  </div>`,
  borderColor: '#808080',
  borderWidth: 1,
  borderRadius: 4,
  padding: 8,
  confine: true,
  backgroundColor: '#FFFFFF',
  extraCssText: 'box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);',
});

const labelConfig = {
  show: true,
  position: 'inner',
  formatter: params => (params.value ? `${params.value}%` : ''),
  color: '#FFFFFF',
  fontSize: 16,
  lineHeight: 20,
  fontFamily: 'Lato-Bold, sans-serif',
};

export default {
  name: 'DoughnutChart',
  components: { Tooltip },
  props: {
    id: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: [],
    },
    static: {
      type: Boolean,
      default: false,
    },
    tooltipTitle: {
      type: String,
      default: '',
    },
    config: {
      type: Object,
      default: () => ({
        large: false,
        labelPosition: 'right', // position of label
        labelStyle: 'column', // style "row" or  "column"
        total: null,
        legendStyles: {
          fontSize: '10px',
          boxWidth: '25px',
          boxHeight: '13px',
          gap: '12px',
        },
      }),
    },
    stubDescription: { type: String, required: false, default: '' },
    withDelay: { type: Boolean, required: false, default: true },
  },
  emits: {
    onCheckSelected: null,
  },
  data() {
    return {
      timeout: null,
      itemsSelectState: {},
      isOverflowed: {},
      stubDescPosition: { top: 0, left: 0 },
      snapshotTimeout: null,
    };
  },
  computed: {
    chartItems() {
      return this.items.map(item => ({
        ...item,
        originalName: item.name,
        name: `${item.name}#${item.id}`,
      }));
    },
    options() {
      return {
        legend: {
          show: false,
        },
        tooltip: this.static ? false : tooltipConfig(this.tooltipTitle),
        series: [{
          name: this.id,
          type: 'pie',
          radius: ['54%', '96%'],
          right: 0,
          itemStyle: {
            borderWidth: 1,
            borderRadius: 4,
            borderColor: '#FFFFFF',
            color: params => params.data.color,
          },
          label: this.static ? false : labelConfig,
          emphasis: (this.static ? false : {
            scale: true,
            scaleSize: 4,
            itemStyle: {
              borderWidth: 4,
              borderRadius: 8,
            },
          }),
          labelLine: {
            show: false,
          },
          data: this.chartItems,
          animation: !this.static,
          silent: this.static,
        }],
      };
    },
    labelPosition() {
      if (this.config.labelPosition === 'top') {
        return 'doughnut_chart_wrapper_top';
      } if (this.config.labelPosition === 'bottom') {
        return 'doughnut_chart_wrapper_bottom';
      } if (this.config.labelPosition === 'left') {
        return 'doughnut_chart_wrapper_left';
      }

      return 'doughnut_chart_wrapper_right';
    },
    totalLabelStyles() {
      return this.static ? { opacity: 0, visibility: 'hidden' } : {};
    },
    showStubDescription() {
      return this.stubDescription.length && this.static;
    },
    aggrQuantity() {
      return this.items.reduce((acc, item) => acc + item.qty || 0, 0);
    },
  },
  watch: {
    items() {
      this.updateItems();
    },
  },
  mounted() {
    if (this.$refs.legendName && this.$refs.legendName.length) {
      this.$refs.legendName.forEach(elem => {
        if (elem.clientWidth === 200) {
          this.isOverflowed = {
            ...this.isOverflowed,
            [elem.textContent]: true,
          };
        }
      });
    }

    this.instance = echarts.init(this.$refs.chart);
    this.instance.setOption(this.options);
    this.initInstanceListener();

    setTimeout(() => {
      if (this.stubDescription.length) {
        this.calcDescriptionPos();
      }
    }, 0);
  },
  beforeUnmount() {
    this.instance.off('legendselectchanged');
    echarts.dispose(this.instance);
  },
  methods: {
    initInstanceListener() {
      this.instance.on('legendselectchanged', event => {
        this.itemsSelectState = event.selected;
        let hasSelected = false;

        Object.values(event.selected).forEach(val => {
          if (val) {
            hasSelected = true;
          }
        });

        this.$emit('onCheckSelected', hasSelected);
      });
    },
    updateItems() {
      const delay = this.withDelay ? 1000 : 0;

      clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        this.instance.setOption({
          ...this.options,
        });

        const selected = this.instance.getOption()?.legend[0]?.selected || {};
        const selectedValues = Object.values(selected);

        let hasSelected = true;

        if (selectedValues.length) {
          hasSelected = false;
          selectedValues.forEach(val => {
            if (val) {
              hasSelected = true;
            }
          });
        }

        this.$emit('onCheckSelected', hasSelected);
      }, delay);
    },
    isGrayedOut(name) {
      return this.itemsSelectState[name] !== undefined && !this.itemsSelectState[name];
    },
    toggleSelect(name) {
      if (this.static) {
        return;
      }

      this.instance.dispatchAction({
        type: 'legendToggleSelect',
        // legend name
        name,
      });
    },
    highlightSeries(name) {
      if (this.static) {
        return;
      }

      this.instance.dispatchAction({
        type: 'highlight',
        seriesName: this.id,
        name,
      });
    },
    downplay(name) {
      if (this.static) {
        return;
      }

      this.instance.dispatchAction({
        type: 'downplay',
        seriesName: this.id,
        name,
      });
    },
    calcDescriptionPos() {
      const pos = {
        top: 0,
        left: 0,
      };

      const chart = this.$refs.chart;

      if (chart) {
        const chartCoords = chart.getBoundingClientRect();
        const left = chartCoords.left + (chartCoords.width / 2);
        const top = chartCoords.top + (chartCoords.height / 2);

        pos.top = `${top}px`;
        pos.left = `${left}px`;
      }

      this.stubDescPosition = pos;
    },
  },
};
</script>

<style module src="./less/doughnutChart.less"></style>
